import GetWorkflow from '@/graphql/queries/GetWorkflow'
import OnAddCardComment from '@/graphql/subscriptions/OnAddCardComment'
import OnUpdateCardComment from '@/graphql/subscriptions/OnUpdateCardComment'
import OnDeleteCardComment from '@/graphql/subscriptions/OnDeleteCardComment'
import OnToggleCardReaction from '@/graphql/subscriptions/OnToggleCardReaction'
import OnCreateCardAttachment from '@/graphql/subscriptions/OnCreateCardAttachment'
import OnDeleteCardAttachment from '@/graphql/subscriptions/OnDeleteCardAttachment'
import OnUpdateCards from '@/graphql/subscriptions/OnUpdateCards'
import UpdateWorkflow from '@/graphql/mutations/UpdateWorkflow'
import UpdateWorkflowName from '@/graphql/mutations/UpdateWorkflowName'
import UpdateWorkflowNoHistory from '@/graphql/mutations/UpdateWorkflowNoHistory'
import UpdateCards from '@/graphql/mutations/UpdateCards'
import ImportWorkflow from '@/graphql/mutations/ImportWorkflow'
import MoveCard from '@/graphql/mutations/MoveCard'
import DeleteCard from '@/graphql/mutations/DeleteCard'
import RestorePreviousWorkflowVersion from '@/graphql/mutations/RestorePreviousWorkflowVersion'
import AddCardComment from '@/graphql/mutations/AddCardComment'
import UpdateCardComment from '@/graphql/mutations/UpdateCardComment'
import DeleteCardComment from '@/graphql/mutations/DeleteCardComment'
import ToggleCardReaction from '@/graphql/mutations/ToggleCardReaction'
import CreateCardAttachment from '@/graphql/mutations/CreateCardAttachment'
import DeleteCardAttachment from '@/graphql/mutations/DeleteCardAttachment'
import { EventBus } from '@/event-bus/event-bus.js'
import client from '@/graphql/client'
import { addToSubscriptions } from '@/api/subscriptionHelpers'
import { track } from '@/utils/telemetry'

// An id is only usable if it is a real, non-empty string. Cleared workflow state
// (e.g. after cancelling a generation or navigating away) can yield `undefined`,
// which serializes to the literal string "undefined" in the GraphQL request and
// is rejected by the backend as "Not authorized for this project" — surfacing a
// spurious "you don't have permission" toast and a 404. Bail out instead of
// firing a query we know will fail.
const isUsableId = (v) => typeof v === 'string' && v.length > 0 && v !== 'undefined' && v !== 'null'

export const workflow = {
  namespaced: true,
  state: {
    workflow: null,
    loading: false,
    loadingUpdate: false,
    showReleaseNo: null,
    showTagCardType: null,
    subscriptions: null,
    isViewOnlyEnabled: false,
    workflowSettingsOpen: false,
    workflowSettingsTab: null,
    workflowDescriptionDialogOpen: false,
    showRetryDialog: false,
    failedGenerations: [],
    retryCallback: null
  },
  mutations: {
    setWorkflow (state, payload) {
      state.workflow = payload
    },
    setLoading (state, payload) {
      state.loading = payload
    },
    setLoadingUpdate (state, payload) {
      state.loadingUpdate = payload
    },
    setShowReleaseNo (state, payload) {
      state.showReleaseNo = payload
    },
    setShowTagCardType (state, payload) {
      state.showTagCardType = payload
    },
    updateWorkflow (state, payload) {
      // ignore updates coming from other workflows
      if (state.workflow?.id !== payload.id) return

      // Check if this is an actual restore operation (sameAsVersion changed)
      // vs a regular update on a workflow that was previously restored
      const isRestoreOperation = payload.sameAsVersion && (payload.sameAsVersion !== state.workflow?.sameAsVersion)

      if (payload.schemas === null && !isRestoreOperation) {
        delete payload.schemas
      }

      if (payload.domainEvents && !isRestoreOperation) {
        if (!state.workflow.domainEvents) {
          state.workflow.domainEvents = []
        }

        for (const event of payload.domainEvents) {
          if (event.type === 'deleted') {
            state.workflow.domainEvents = state.workflow.domainEvents.filter((it) => it.id !== event.id)
            continue
          }

          const existingEvent = state.workflow.domainEvents.find((it) => it.id === event.id)
          if (existingEvent) {
            if (existingEvent.version < event.version) {
              event.cards = event.cards !== null ? event.cards : existingEvent.cards
              Object.assign(existingEvent, event)
            }
          } else {
            state.workflow.domainEvents.push(event)
          }
        }
      }
      // remove domainEvents from payload to avoid overwriting the state
      // because we have already updated all the events now
      // and if it's empty we it won't change anything anyway
      // because deleting the last event is not returning an empty array
      // it returns the deleted event
      if (!isRestoreOperation) {
        delete payload.domainEvents
      }

      // Handle schema delta merge (when response contains only changed schemas)
      // Delta is detected by: any entry has type === 'deleted', or all IDs exist in current state
      // and count is less than current. Full replacement (addSchemaAndLinkCard, restore) sends all schemas.
      if (payload.schemas && Array.isArray(payload.schemas) && !isRestoreOperation) {
        const currentSchemas = state.workflow.schemas || []
        const hasDeletedMarker = payload.schemas.some((s) => s.type === 'deleted')
        const currentSchemaIds = new Set(currentSchemas.map((s) => s.id))
        const nonDeletedPayloadSchemas = payload.schemas.filter((s) => s.type !== 'deleted')
        const allExistInCurrent = nonDeletedPayloadSchemas.every((s) => currentSchemaIds.has(s.id))
        const isDelta = (hasDeletedMarker && allExistInCurrent) ||
          (nonDeletedPayloadSchemas.length > 0 && allExistInCurrent && nonDeletedPayloadSchemas.length <
            currentSchemas.length)

        if (isDelta) {
          const mergedSchemas = [...currentSchemas]

          for (const schema of payload.schemas) {
            if (schema.type === 'deleted') {
              const idx = mergedSchemas.findIndex((s) => s.id === schema.id)
              if (idx >= 0) {
                mergedSchemas.splice(idx, 1)
              }
              continue
            }

            const existingIdx = mergedSchemas.findIndex((s) => s.id === schema.id)
            if (existingIdx >= 0) {
              mergedSchemas[existingIdx] = schema
            }
          }

          state.workflow.schemas = mergedSchemas
          delete payload.schemas
        }
      }

      if (state.workflow && ((state.workflow.version < payload.version) || (payload.sameAsVersion !== state.workflow.sameAsVersion))) {
        Object.assign(state.workflow || {}, payload)
      }
    },
    updateWorkflowAndAcceptSameVersion (state, payload) {
      if (!state.workflow || state.workflow.version <= payload.version) {
        Object.assign(state.workflow || {}, payload)
      }
    },
    deleteWorkflow (state, payload) {
      if (payload.id === state.workflow?.id) {
        state.workflow = null
      }
    },
    addOrUpdateCardReply (state, payload) {
      const card = findCardInWorkflow(payload.cardId, state.workflow)
      const index = card.comments.findIndex((comment) => comment.id === payload.id)
      if (index >= 0) {
        Object.assign(card.comments[index], payload)
      } else {
        card.comments.push(payload)
      }
    },
    async updateCards (state, { payload, rootState }) {
      const workflow = state.workflow
      for (const card of payload.cards) {
        const cardId = card.cardId
        const eventId = card.eventId

        const eventIndex = workflow.domainEvents.findIndex((event) => event.id === eventId)
        if (eventIndex >= 0) {
          const event = workflow.domainEvents[eventIndex]
          if (!event.cards) {
            event.cards = []
          }

          let cardIndex = event.cards.findIndex((req) => req.id === cardId)
          if (cardIndex === -1) {
            cardIndex = event.cards.length

            // When creating a new card we need all required fields from Card type.
            // But UpdateCardOutput does not give us all required fields
            // so we need to add them manually.
            card.id = cardId
            card.attachments = []
            card.comments = []
            card.reactions = []

            // Add dummy card with version 0 which will be overwritten below
            const newCard = { ...card }
            newCard.version = 0
            event.cards.push(newCard)
          } else {
            const existingCard = event.cards[cardIndex]
            const index = payload?.cards?.findIndex((req) => req.cardId === existingCard.id)
            if (index !== -1) {
              payload.cards[index] = existingCard
              card.creatorId = existingCard.creatorId
              card.creator = existingCard.creator
            }
          }

          // event.cards[cardIndex].version += 5
          // accept old version === null for backward compatibility
          if ((card.version > event.cards[cardIndex].version) || event.cards[cardIndex].version === null) {
            const oldCard = event.cards[cardIndex]
            const oldCardTypeId = oldCard.cardType?.id

            const updatedCard = {
              ...oldCard,
              ...card,
              tags: card.tags !== undefined ? [...(card.tags || [])] : (oldCard.tags || [])
            }

            // Replace the card object with new reference
            event.cards.splice(cardIndex, 1, updatedCard)

            // Using splice ensures Vue detects the change
            const updatedEvent = {
              ...event,
              cards: [...event.cards]
            }
            workflow.domainEvents.splice(eventIndex, 1, updatedEvent)

            if (oldCardTypeId !== card.cardTypeId) {
              const allCardTypes = rootState.cardTypes.cardTypes
              workflow.domainEvents[eventIndex].cards[cardIndex].cardType = allCardTypes.find((it) => it.id === card.cardTypeId)
            }
          }
        }

        delete card.cardId
        delete card.cardIndex
        delete card.eventId
        delete card.eventIndex
      }

      // Check workflow version
      // we don't always want to update the workflow version
      // although we are updating the cards
      // because we might receive partial updates in the wrong order
      if (payload.version > workflow.version) {
        workflow.updatedAt = payload.updatedAt
        workflow.updateNote = payload.updateNote
        workflow.version = payload.version
        workflow.sameAsVersion = payload.sameAsVersion
      }

      // CRITICAL: Replace the entire workflow object to trigger Vue reactivity for computed properties
      // This ensures components that depend on workflow.domainEvents will recalculate
      state.workflow = { ...workflow }
    },
    deleteCardReply (state, payload) {
      const card = findCardInWorkflow(payload.cardId, state.workflow)
      const index = card.comments.findIndex(comment => comment.id === payload.id)
      if (index >= 0) {
        card.comments.splice(index, 1)
      }
    },
    updateCardReaction (state, payload) {
      for (const event of state.workflow.domainEvents) {
        const card = event.cards?.find(({ id }) => id === payload.cardId)
        if (!card) continue
        const reactionIndex = card.reactions.findIndex(({ user }) => user.id === payload.user.id)
        if (reactionIndex === -1) {
          card.reactions.push(payload)
          break
        }
        const reaction = card.reactions[reactionIndex]
        if (reaction.direction === payload.direction) {
          card.reactions.splice(reactionIndex, 1) // delete reaction
        } else {
          card.reactions.splice(reactionIndex, 1, payload)
        }
      }
    },
    addCardAttachment (state, payload) {
      const { cardId } = payload
      const card = findCardInWorkflow(cardId, state.workflow)
      if (card) {
        card.attachments.push(payload)
      }
    },
    deleteCardAttachment (state, payload) {
      const { cardId, ...attachment } = payload
      const card = findCardInWorkflow(cardId, state.workflow)
      if (card) {
        const attachmentIndex = card.attachments.findIndex(existingAttachment => existingAttachment.id === attachment.id)
        if (attachmentIndex >= 0) {
          card.attachments.splice(attachmentIndex, 1)
        }
      }
    },
    setSubscriptions (state, payload) {
      state.subscriptions = payload
    },
    unsubscribeFromAll (state) {
      if (state.subscriptions) {
        for (const subscription of state.subscriptions) {
          subscription.unsubscribe()
        }
      }
    },
    setViewOnlyMode (state, data) {
      state.isViewOnlyEnabled = data
    },
    setWorkflowSettingsOpen (state, payload) {
      state.workflowSettingsOpen = payload
    },
    setWorkflowSettingsTab (state, payload) {
      state.workflowSettingsTab = payload
    },
    setWorkflowDescriptionDialogOpen (state, payload) {
      state.workflowDescriptionDialogOpen = payload
    },
    setShowRetryDialog (state, payload) {
      state.showRetryDialog = payload
    },
    setFailedGenerations (state, payload) {
      state.failedGenerations = payload
    },
    addFailedGeneration (state, payload) {
      state.failedGenerations.push(payload)
      // Telemetry: surface generation failures server-side (which step, which
      // model, why) so we can diagnose without the user's console. Fire-and-forget.
      track('generation_failure', {
        step: payload?.type || payload?.step || 'unknown',
        aiVersion: state.workflow?.aiVersion,
        role: payload?.role,
        event: payload?.eventDescription,
        errorName: payload?.errorName,
        message: String(payload?.error?.message || payload?.message || payload?.error || '').slice(0, 400)
      })
    },
    clearFailedGenerations (state) {
      state.failedGenerations = []
    },
    setRetryCallback (state, payload) {
      state.retryCallback = payload
    }
  },
  actions: {
    async updateWorkflow ({ commit, dispatch, getters }, { projectId, input }) {
      try {
        const res = await client.query({
          operationName: 'updateWorkflow',
          query: UpdateWorkflow,
          variables: {
            projectId,
            input
          }
        })
        commit('updateWorkflow', res.updateWorkflow)
        return res
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: input.id, projectId }, e, commit, dispatch)
      }
    },

    async updateWorkflowName ({ commit, dispatch }, { projectId, input }) {
      try {
        const res = await client.query({
          operationName: 'updateWorkflowName',
          query: UpdateWorkflowName,
          variables: {
            projectId,
            input
          }
        })

        commit('updateWorkflow', res.updateWorkflowName)
        return res
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: input.id, projectId }, e, commit, dispatch)
      }
    },

    async importWorkflow ({ commit, dispatch }, { workflowId, projectId, workflowData }) {
      try {
        const res = await client.query({
          operationName: 'importWorkflow',
          query: ImportWorkflow,
          variables: {
            workflowId,
            projectId,
            workflowData
          }
        })

        commit('setWorkflow', res.importWorkflow)
        commit('snackbar/showMessage', { content: 'Workflow imported successfully' }, { root: true })
        return res
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId, projectId }, e, commit, dispatch)
        throw e
      }
    },

    async updateWorkflowNoHistory ({ commit, dispatch, getters }, { projectId, input }) {
      if (!projectId) {
        // avoid error when projectId is null
        // which happens when user logs out
        // in the cypress tests
        return
      }
      try {
        const res = await client.query({
          operationName: 'updateWorkflowNoHistory',
          query: UpdateWorkflowNoHistory,
          variables: {
            projectId,
            input
          }
        })
        commit('updateWorkflowAndAcceptSameVersion', res.updateWorkflowNoHistory)
        return res
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: input.id, projectId }, e, commit, dispatch)
      }
    },

    async restorePreviousWorkflowVersion ({ commit, dispatch, getters }, { restoreVersion, workflow }) {
      try {
        const res = await client.query({
          operationName: 'restorePreviousWorkflowVersion',
          query: RestorePreviousWorkflowVersion,
          variables: {
            projectId: workflow.projectId,
            input: {
              id: workflow.id,
              expectedVersion: workflow.version,
              restorePreviousWorkflowVersion: restoreVersion,
              sameAsVersion: workflow.sameAsVersion
            }
          }
        })
        commit('updateWorkflow', res.restorePreviousWorkflowVersion)
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: workflow.id, projectId: workflow.projectId }, e, commit, dispatch)
      }
    },

    async updateCards ({ commit, state, rootState, dispatch }, { input }) {
      try {
        const res = await client.query({
          operationName: 'updateCards',
          query: UpdateCards,
          variables: {
            projectId: state.workflow.projectId,
            input
          }
        })
        commit('updateCards', { payload: res.updateCards, rootState })
        return res.updateCards
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: input.workflowId, projectId: state.workflow.projectId }, e, commit, dispatch)
      }
    },

    async refreshCardTypes ({ commit, state, rootState, dispatch }, { input }) {
      try {
        const { workflow } = state

        if (!isUsableId(workflow?.id) || !isUsableId(workflow?.projectId)) {
          return
        }

        const res = await client.query({
          operationName: 'getWorkflow',
          query: GetWorkflow,
          variables: {
            workflowId: workflow.id,
            projectId: workflow.projectId
          }
        })

        commit('setWorkflow', res.getWorkflow)
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: input.workflowId, projectId: state.workflow.projectId }, e, commit, dispatch)
      }
    },

    async moveCard ({ commit, state, rootGetters, dispatch }, { input }) {
      try {
        const res = await client.query({
          operationName: 'moveCard',
          query: MoveCard,
          variables: {
            projectId: state.workflow.projectId,
            input
          }
        })
        const workflow = res.moveCard
        commit('updateWorkflow', workflow)
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: input.workflowId, projectId: state.workflow.projectId }, e, commit, dispatch)
      }
    },

    async deleteCard ({ commit, state, rootGetters, dispatch }, { input }) {
      try {
        const res = await client.query({
          operationName: 'deleteCard',
          query: DeleteCard,
          variables: input
        })
        commit('updateWorkflow', res.deleteCard)
      } catch (e) {
        showErrorMessageAndResetDiagram({ workflowId: input.workflowId, projectId: state.workflow.projectId }, e, commit, dispatch)
      }
    },

    async addCardReply ({ commit, state, rootGetters, dispatch }, { workflowId, cardId, input }) {
      try {
        const res = await client.query({
          operationName: 'addCardComment',
          query: AddCardComment,
          variables: {
            workflowId,
            cardId,
            input
          }
        })
        commit('addOrUpdateCardReply', res.addCardComment)
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'Error saving, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async updateCardReply ({ commit, state, rootGetters, dispatch }, { workflowId, cardId, id, input }) {
      try {
        const res = await client.query({
          operationName: 'updateCardComment',
          query: UpdateCardComment,
          variables: {
            workflowId,
            cardId,
            id,
            input
          }
        })
        commit('addOrUpdateCardReply', res.updateCardComment)
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'Error saving, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async deleteCardReply ({ commit, state, rootGetters, dispatch }, { workflowId, cardId, id }) {
      try {
        await client.query({
          operationName: 'deleteCardComment',
          query: DeleteCardComment,
          variables: {
            workflowId,
            cardId,
            id
          }
        })
        // update on subscription
        commit('snackbar/showMessage', { content: 'Deleted' }, { root: true })
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'Error saving, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async toggleCardReaction ({ commit, state, rootGetters, dispatch }, { workflowId, input }) {
      try {
        await client.query({
          operationName: 'toggleCardReaction',
          query: ToggleCardReaction,
          variables: {
            workflowId,
            input
          }
        })
        // update on subscription
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'Error saving, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async createCardAttachment ({ commit, state, rootGetters, dispatch }, { workflowId, input }) {
      try {
        await client.query({
          operationName: 'createCardAttachment',
          query: CreateCardAttachment,
          variables: {
            workflowId,
            input
          }
        })
        // update on subscription
        commit('snackbar/showMessage', { content: 'File uploaded' }, { root: true })
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'Error, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async deleteCardAttachment ({ commit, state, rootGetters, dispatch }, { workflowId, id }) {
      try {
        await client.query({
          operationName: 'deleteCardAttachment',
          query: DeleteCardAttachment,
          variables: {
            workflowId,
            id
          }
        })
        // update on subscription
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'Error, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async getWorkflow ({ commit, state, rootState, rootGetters, dispatch }, params) {
      if (!isUsableId(params?.id) || !isUsableId(params?.projectId)) {
        return
      }
      try {
        const res = await client.query({
          operationName: 'getWorkflow',
          query: GetWorkflow,
          variables: {
            workflowId: params.id,
            projectId: params.projectId
          }
        })
        commit('setWorkflow', res.getWorkflow)

        // Set view-only mode to mirror the backend org-RBAC write gate (which is the
        // real enforcement). Editable when the caller is a platform admin/SuperAdmin or
        // has a write role (OWNER/MEMBER) in the project's org; VIEWERs are read-only.
        // NOTE: the legacy `projectMemberships` TeamMember check this replaced no longer
        // works — org users have no TeamMember row, and the items no longer carry a
        // userId — so it forced everyone except `adminUser` into view-only.
        if (!rootState.isUserLoggedIn) {
          commit('setViewOnlyMode', true)
        } else {
          const activeProject = rootState.projectMemberships?.activeProject
          const projectMatches = activeProject?.id === params.projectId
          const orgId = projectMatches ? activeProject.organisationId : null
          const orgCreatorUserId = projectMatches ? activeProject?.organisation?.userId : null
          const canWrite = rootGetters['orgMembers/canWriteInOrg'](orgId, orgCreatorUserId)
          commit('setViewOnlyMode', !canWrite)
        }
        commit('unsubscribeFromAll')
        if (state.isViewOnlyEnabled) {
          // dont' subscribe if user is not logged in
          return res.getWorkflow
        }
        const subscriptions = []
        const addSubscription = (query, variables, callback) => {
          const sub = addToSubscriptions(query, variables, callback)
          subscriptions.push(sub)
        }

        // OnCreateOrUpdateWorkflow and OnDeleteWorkflow subscriptions removed
        // Both are handled by workflows module which notifies this module via cross-module commit

        addSubscription(OnAddCardComment, { workflowId: params.id }, (data) => {
          commit('addOrUpdateCardReply', data.onAddCardComment)
        })

        addSubscription(OnUpdateCardComment, { workflowId: params.id }, (data) => {
          commit('addOrUpdateCardReply', data.onUpdateCardComment)
        })

        addSubscription(OnDeleteCardComment, { workflowId: params.id }, (data) => {
          commit('deleteCardReply', data.onDeleteCardComment)
        })

        addSubscription(OnToggleCardReaction, { workflowId: params.id }, (data) => {
          commit('updateCardReaction', data.onToggleCardReaction)
        })

        addSubscription(OnCreateCardAttachment, { workflowId: params.id }, (data) => {
          commit('addCardAttachment', data.onCreateCardAttachment)
        })

        addSubscription(OnDeleteCardAttachment, { workflowId: params.id }, (data) => {
          commit('deleteCardAttachment', data.onDeleteCardAttachment)
        })

        addSubscription(OnUpdateCards, { workflowId: params.id }, (data) => {
          commit('updateCards', { payload: data.onUpdateCards, rootState })
          commit('setWsMessageForCypress', { version: data.onUpdateCards.version }, { root: true })
        })

        commit('setSubscriptions', subscriptions)

        return res.getWorkflow
      } catch (e) {
        console.error('unauthorized')
        commit('setWorkflow', null)
      }
    },

    async getWorkflowDetails (_, params) {
      if (!isUsableId(params?.id) || !isUsableId(params?.projectId)) {
        return null
      }
      try {
        const res = await client.query({
          operationName: 'getWorkflow',
          query: GetWorkflow,
          variables: {
            workflowId: params.id,
            projectId: params.projectId
          }
        })

        return res.getWorkflow
      } catch (e) {
        console.error('unauthorized')
      }
    },

    openWorkflowSettings ({ commit }, { tab = null } = {}) {
      commit('setWorkflowSettingsTab', tab)
      commit('setWorkflowSettingsOpen', true)
    },

    closeWorkflowSettings ({ commit }) {
      commit('setWorkflowSettingsTab', null)
      commit('setWorkflowSettingsOpen', false)
    },

    openWorkflowDescriptionDialog ({ commit }) {
      commit('setWorkflowDescriptionDialogOpen', true)
    },

    openRetryDialog ({ commit }, { failedItems = [], retryCallback = null } = {}) {
      commit('setFailedGenerations', failedItems)
      commit('setRetryCallback', retryCallback)
      commit('setShowRetryDialog', true)
    },

    closeRetryDialog ({ commit }) {
      commit('setShowRetryDialog', false)
      commit('clearFailedGenerations')
      commit('setRetryCallback', null)
    },

    async retryFailedGenerations ({ state, commit }) {
      if (state.retryCallback && typeof state.retryCallback === 'function') {
        commit('setShowRetryDialog', false)
        await state.retryCallback()
      }
    },

    addFailedGeneration ({ commit }, payload) {
      commit('addFailedGeneration', payload)
    },

    clearFailedGenerations ({ commit }) {
      commit('clearFailedGenerations')
    }
  },
  getters: {
    getLoadingState: (state) => state.loading,
    getLoadingUpdate: (state) => state.loadingUpdate,
    sortedCards: (state) => {
      const cards = []
      for (const event of state.workflow.domainEvents) {
        if (event.cards) {
          Array.prototype.push.apply(cards, event.cards)
        }
      }
      return sortCards(cards)
    }
  }
}

const findCardInWorkflow = (cardId, workflow) => {
  let allCards = []
  workflow.domainEvents.forEach(({ cards }) => {
    if (cards) {
      allCards = allCards.concat(cards)
    }
  })
  return allCards.find(({ id }) => id === cardId)
}

const showErrorMessageAndResetDiagram = ({ workflowId, projectId }, e, commit, dispatch) => {
  const isConflict = e?.message?.includes('ConditionalCheckFailedException') ||
    e?.message?.includes('The conditional request failed')
  const message = isConflict
    ? 'This item was modified by another user. Refreshing with the latest version.'
    : 'Error saving, try again!'

  commit('snackbar/showMessage', {
    content: message,
    timeout: 6000,
    color: 'red',
    location: 'center center'
  }, { root: true })
  console.error(e)
  if (isUsableId(workflowId) && isUsableId(projectId)) {
    dispatch('getWorkflow', { id: workflowId, projectId })
  }
  EventBus.$emit('reset-diagram')
}

const sortCards = (cards) => {
  cards.sort((a, b) => {
    const sortA = a.sortkey || 'zzzzzzzzzz'
    const sortB = b.sortkey || 'zzzzzzzzzz'
    const releaseA = a.releaseNo || '9999999999'
    const releaseB = b.releaseNo || '9999999999'
    if (releaseA > releaseB) {
      return 1
    } else if (releaseB > releaseA) {
      return -1
    } else {
      if (sortA === sortB) {
        return a.id > b.id ? 1 : -1
      } else {
        return sortA > sortB ? 1 : -1
      }
    }
  })
  return cards
}
